import { Link } from 'react-router-dom'
import { useAttributionCapture } from '@/lib/attribution'
import { C } from '../lib/landingColors'
import type { MarketingLocale } from '../lib/marketingLocale'
import { LandingNav } from './LandingNav'
import { LandingFooter } from './LandingFooter'
import { MarketingLanguageSwitch } from './MarketingLanguageSwitch'

/**
 * Chrome shared by every standalone marketing page (/press, /founding,
 * /legal/subprocessors, the comparison pages).
 *
 * The landing page does NOT use this — it composes its own sections and needs
 * the hero to sit under the transparent nav. Everything else is a document, so
 * it gets the nav, a max-width column and the footer with the legal links.
 *
 * `locale` is the page's marketing language, which on these routes comes from
 * the URL rather than from the visitor's UI locale. It is threaded into the nav
 * and the footer so the chrome speaks the same language as the document it
 * frames — a German press page under an English navigation reads like a
 * half-finished translation — and so the footer's own links point at the
 * same-language siblings. Omitted on the landing page, where the chrome still
 * follows the visitor.
 */
export function MarketingShell({ locale, children }: { locale?: MarketingLocale; children: React.ReactNode }) {
  // A comparison page is often a visitor's very first StudioFlow URL, so first-touch
  // capture has to happen here, not only on the landing page (WS3.4).
  useAttributionCapture()

  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh' }}>
      <LandingNav locale={locale} />
      {/* pt-28 clears the fixed nav; the nav itself carries the PWA safe-area padding. */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <MarketingLanguageSwitch />
        {children}
      </main>
      <LandingFooter locale={locale} />
    </div>
  )
}

/** Page title — the H1 every marketing document opens with. */
export function MarketingTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6" style={{ color: C.text }}>
      {children}
    </h1>
  )
}

/** Section heading used across the marketing documents. */
export function MarketingHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl sm:text-3xl font-bold mt-14 mb-4" style={{ color: C.text }}>
      {children}
    </h2>
  )
}

/** One level down — the 5.1 / 5.2 subsections of a legal document. */
export function MarketingSubheading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg sm:text-xl font-semibold mt-8 mb-3" style={{ color: C.text }}>
      {children}
    </h3>
  )
}

/**
 * Emphasis inside document prose, in marketing-palette colours.
 *
 * `<strong>` inherits the muted body colour, which on this background makes a
 * bolded warning quieter than the sentence around it. The custom property is
 * how a Tailwind arbitrary value gets at a palette constant — the class text
 * has to be static for the JIT to see it.
 */
const PROSE = '[&_strong]:font-semibold [&_strong]:text-[var(--sf-prose-strong)]'
const PROSE_VARS = { color: C.muted, '--sf-prose-strong': C.text } as React.CSSProperties

/** Body paragraph — the marketing pages are prose, so this is most of the page. */
export function MarketingParagraph({ children }: { children: React.ReactNode }) {
  return (
    <p className={`text-base sm:text-lg leading-relaxed mb-4 ${PROSE}`} style={PROSE_VARS}>
      {children}
    </p>
  )
}

/**
 * Bulleted list for the document pages.
 *
 * The legal pages used to render their lists through Tailwind's `prose` plugin,
 * whose colours come from the app's light/dark class. A marketing page is dark
 * on every theme, so `prose-invert` was correct exactly half the time — in a
 * light-themed browser the terms page rendered near-black text on the marketing
 * background. These take their colours from the marketing palette directly.
 */
export function MarketingList({ children }: { children: React.ReactNode }) {
  return <ul className="space-y-2.5 mb-4">{children}</ul>
}

export function MarketingListItem({ children }: { children: React.ReactNode }) {
  return (
    <li className={`flex gap-3 text-base sm:text-lg leading-relaxed ${PROSE}`} style={PROSE_VARS}>
      <span aria-hidden="true" style={{ color: C.primary }}>—</span>
      <span>{children}</span>
    </li>
  )
}

/** Inline link inside document prose. */
export function MarketingLink({ to, children }: { to: string; children: React.ReactNode }) {
  const className = 'underline underline-offset-4 hover:opacity-80'
  const style = { color: C.accent }
  return to.startsWith('mailto:') || to.startsWith('http')
    ? <a href={to} className={className} style={style}>{children}</a>
    : <Link to={to} className={className} style={style}>{children}</Link>
}
